// Static `?state=` frames freeze the page on a single resolved view —
// useful for design review screenshots. Transition-driven flows use
// `?simulate=enroll|connect|full` (see useYaasRetailFlow) instead.
//
// Three frames sit on the enrollment timeline and are easy to confuse:
//   pending  — applied, position not yet activated ($0 earned)
//   opted-in — just joined an opt-in Program: Earning, but day zero,
//              so still $0 earned and no distribution behind them
//   active   — has been earning for a while (accrued rewards, history)
//
// gold-lp is a single GOLD-USDv Raydium LP position ($150,000.00),
// Earning — the position surface with a DEX LP rather than a wallet
// holding. gold-lp-wallet adds a $10,500.37 USDv wallet position
// beside it.
//
// Frames also drive the distributions data, not just the resolved view:
// `mocks/demoDistributions.ts` is the fetch-boundary fixture the
// Transactions view renders against (active / just-distributed populated,
// opted-in / pending empty, everything else falls through to yaas-api).
export type DemoState =
    | "disconnected"
    | "connected-no-yaas"
    | "connected-no-balance"
    | "connected-stables-only"
    | "connected-usdv-only"
    | "apply-below-min"
    | "pending"
    | "opted-in"
    | "active"
    | "just-distributed"
    | "below-min"
    | "disabled"
    | "gold-lp"
    | "gold-lp-wallet"
    | null;

export function readDemoState(): DemoState {
    if (typeof window === "undefined") return null;
    const value = new URLSearchParams(window.location.search).get("state");
    if (
        value === "disconnected" ||
        value === "connected-no-yaas" ||
        value === "connected-no-balance" ||
        value === "connected-stables-only" ||
        value === "connected-usdv-only" ||
        value === "apply-below-min" ||
        value === "pending" ||
        value === "opted-in" ||
        value === "active" ||
        value === "just-distributed" ||
        value === "below-min" ||
        value === "disabled" ||
        value === "gold-lp" ||
        value === "gold-lp-wallet"
    ) {
        return value;
    }
    return null;
}

/**
 * `?mode=stream|claim` — pins the committed distribution mode for a
 * demo frame. Without it a `?state=` frame always reads as Stream: the
 * page's local default is "stream" and the demo states never resolve a
 * server-side `distributionMode` to adopt, so Claim Mode was
 * unreachable from a URL. Null means "no override — follow the server".
 */
export function readDemoDistributionMode(): "stream" | "claim" | null {
    if (typeof window === "undefined") return null;
    const value = new URLSearchParams(window.location.search).get("mode");
    return value === "stream" || value === "claim" ? value : null;
}
